/**
 * Calendar Source API Contracts (super-admin manage-school tab).
 *
 * Two config layers per school:
 *  - Layer 1 — the SOURCE selector (`blackbaud` | `ical`), written via the
 *    existing screen-features PATCH (`calendar.config.integration`).
 *  - Layer 2 — the iCal FEED URL, stored in SystemConfiguration as `{ url }`
 *    (see KTD1) and read ONLY when the source is `ical`.
 *
 * Endpoints (all super-admin gated, explicit `school_id`):
 *  - GET  /v1/schools/:school_id/calendar-source        → resolved source + feed URL
 *  - PUT  /v1/schools/:school_id/calendar-source/feed-url → write/clear the feed URL
 *  - POST /v1/schools/:school_id/calendar-source/test-feed → category-aware verdict
 */

import { z } from 'zod'

import { normalizeCalendarFeedUrl } from '../../security/safe-url'

/** Calendar source selector. Mirrors the backend `CalendarIntegrationSchema`. */
export const CalendarSourceSchema = z.enum(['blackbaud', 'ical'])
export type CalendarSource = z.infer<typeof CalendarSourceSchema>

// ----- GET resolved calendar source -----

export const GetCalendarSourceResponseSchema = z.object({
  /** The school's effective calendar source. */
  source: CalendarSourceSchema,
  /** The configured iCal feed URL, or null when none is set. */
  feedUrl: z.string().nullable()
})
export type GetCalendarSourceResponse = z.infer<typeof GetCalendarSourceResponseSchema>

// ----- PUT feed URL (write or clear) -----

/**
 * Write request. A blank/empty `feedUrl` CLEARS the stored feed URL (so the
 * `resolveCalendarSource` override stops force-iCal-ing the school). A non-empty
 * URL must be https — the adapter re-guards on fetch, this is the cheap
 * pre-check.
 *
 * A `webcal://` / `webcals://` address is rewritten to its https twin BEFORE
 * that check (SCH-1506): it is the scheme a calendar app hands an admin when
 * they copy a subscribe link, and it names the same document. The value that
 * reaches the use case — and therefore the stored config, the audit entry and
 * the response — is the normalized https form, so a re-paste of the same
 * calendar in either scheme is the same stored string. `http://` stays refused.
 */
export const PutCalendarFeedUrlRequestSchema = z.object({
  feedUrl: z
    .string()
    .trim()
    .transform(normalizeCalendarFeedUrl)
    .refine(value => value === '' || /^https:\/\//i.test(value), {
      message: 'Feed URL must use https'
    })
})
export type PutCalendarFeedUrlRequest = z.infer<typeof PutCalendarFeedUrlRequestSchema>

export const PutCalendarFeedUrlResponseSchema = z.object({
  /** The stored feed URL after the write, or null when cleared. */
  feedUrl: z.string().nullable()
})
export type PutCalendarFeedUrlResponse = z.infer<typeof PutCalendarFeedUrlResponseSchema>

// ----- POST test feed -----

/**
 * Test request. Same `webcal://` normalization as the write (SCH-1506) so the
 * URL an admin tests is the URL they are about to save; the backend's own
 * re-validation then sees an https URL.
 */
export const TestIcalFeedRequestSchema = z.object({
  feedUrl: z.string().trim().min(1, 'Feed URL is required').transform(normalizeCalendarFeedUrl)
})
export type TestIcalFeedRequest = z.infer<typeof TestIcalFeedRequestSchema>

/**
 * Category-aware verdict (KTD3):
 *  - `ok`          — feed fetched + parsed; `eventCount` events in the window.
 *  - `unreachable` — reachability failure (blocked / fetch error). WARN, allow save.
 *  - `malformed`   — fetched but not a valid iCal body. BLOCK save.
 */
export const TestIcalFeedResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), eventCount: z.number().int().nonnegative() }),
  z.object({ status: z.literal('unreachable') }),
  z.object({ status: z.literal('malformed') })
])
export type TestIcalFeedResponse = z.infer<typeof TestIcalFeedResponseSchema>
